import React from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { Input, InputProps } from './components/input/input'
import { TravelInputModeEnum } from './components/input/travel-input-mode.enum'
import { List } from './components/list/list'
import { ListMostBookedRegions } from './components/list/list-most-booked-region'
import { MostBookedRegionType } from './location-picker.type'
import { getListMessage } from './location-picker.utils'
import { ModalPickerRef } from '../../forms'
import { Inputs } from '../../molecules/search-city-dates-adults-v2/useFieldNavigator'

type PlaceListProps = {
  placePredictions: google.maps.places.AutocompletePrediction[]
  isPlacePredictionsLoading: boolean
  onPlaceSelected: (option: any) => void
  isMobile: boolean
  shortLabel: boolean
  searchQuery?: string
  revampPicker: boolean
  isLocationSelected: boolean
}

interface LocationInput extends InputProps {
  isEditing: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  revampPicker: boolean
}

type Handlers = {
  onEditing: (value: boolean) => void
  onSearchQuery: (value?: string) => void
  onLocationSelected: (value: boolean) => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onValid?: (value: keyof Inputs) => void
}

type State = {
  searchQueryIsEmpty: boolean
  isEditing: boolean
  isMobile: boolean
  shortLabel: boolean
  searchQuery?: string
  revampPicker: boolean
  placePredictions: google.maps.places.AutocompletePrediction[]
  isPlacePredictionsLoading: boolean
  mostBookedRegions?: MostBookedRegionType[]
  isLocationSelected: boolean
}

interface LocationPickerContentModalProps {
  field: ControllerRenderProps<FieldValues, string>
  handlers: Handlers
  modalRef?: React.RefObject<ModalPickerRef>
  inputRef?: React.RefObject<HTMLInputElement>
  state: State
}

interface LocationInput extends InputProps {
  isEditing: boolean
  inputRef?: React.RefObject<HTMLInputElement>
}

type PlaceOption = google.maps.places.AutocompletePrediction
type RegionOption = MostBookedRegionType
type OptionType = PlaceOption | RegionOption
function isRegionOption(option: OptionType): option is RegionOption {
  return 'placeId' in option && 'label' in option
}

const getLabel = (option: OptionType, shortLabel: boolean): string => {
  if (isRegionOption(option)) {
    return option.label
  }
  if (shortLabel) {
    return option.structured_formatting.main_text
  }
  return option.description
}

const LocationInput: React.FC<LocationInput> = ({
  isMobile,
  isEditing,
  onChange,
  onReset,
  inputRef,
  revampPicker,
  label,
}) => {
  if (!isMobile || (!isEditing && !revampPicker)) {
    return null
  }

  return (
    <Input
      onChange={onChange}
      onReset={onReset}
      ref={inputRef}
      isMobile={isMobile}
      revampEnabled={revampPicker}
      label={label}
      mode={TravelInputModeEnum.HOUSE_SEARCH}
    />
  )
}

const PlaceList: React.FC<PlaceListProps> = ({
  placePredictions,
  isPlacePredictionsLoading,
  onPlaceSelected,
  isMobile,
  shortLabel,
  searchQuery,
  revampPicker,
  isLocationSelected,
}) => {
  const mappedPlacePredictions = placePredictions.map((item) => ({
    ...item,
    description: shortLabel
      ? item.structured_formatting.main_text
      : item.description,
  }))

  const listMessage = React.useMemo(
    () =>
      getListMessage({
        searchQuery,
        isLoading: isPlacePredictionsLoading,
        hasResults: placePredictions.length > 0,
      }),
    [searchQuery, isPlacePredictionsLoading, placePredictions],
  )

  if (isMobile && revampPicker && isLocationSelected) {
    return null
  }

  return (
    <List
      message={listMessage}
      items={mappedPlacePredictions}
      onItemSelected={onPlaceSelected}
      isMobile={isMobile}
      revampEnabled={revampPicker}
    />
  )
}

export const LocationPickerContentModal: React.FC<
  LocationPickerContentModalProps
> = ({
  field,
  modalRef,
  inputRef,
  state: {
    isEditing,
    isMobile,
    searchQueryIsEmpty,
    isPlacePredictionsLoading,
    placePredictions,
    shortLabel,
    revampPicker,
    mostBookedRegions = [],
    searchQuery,
    isLocationSelected,
  },
  handlers: {
    onEditing,
    onSearchQuery,
    onInputChange,
    onLocationSelected,
    onValid,
  },
}) => {
  const handleOptionSelected = React.useCallback(
    (option: OptionType) => {
      const placeId = isRegionOption(option) ? option.placeId : option.place_id
      const label = getLabel(option, shortLabel)

      field.onChange({ placeId, label })
      onLocationSelected(true)
      onValid?.('location')

      modalRef?.current?.setShowModal(false)

      if (isMobile && revampPicker) {
        onEditing(false)
      }
    },
    [field, onLocationSelected, modalRef, isMobile, revampPicker, shortLabel],
  )

  const onReset = React.useCallback(() => {
    onSearchQuery(undefined)
    field.onChange(null)
    onLocationSelected(false)
    onEditing(false)
  }, [onSearchQuery, field, onLocationSelected, onEditing])

  const inputLabel = React.useMemo(() => {
    if (!field?.value?.label) {
      return undefined
    }

    return field.value.label
  }, [field?.value?.label])

  return (
    <div className={s.contentModalContainer}>
      <LocationInput
        isMobile={isMobile}
        isEditing={isEditing}
        onChange={onInputChange}
        onReset={onReset}
        inputRef={inputRef}
        revampPicker={revampPicker}
        label={inputLabel}
      />
      {!searchQueryIsEmpty && (
        <PlaceList
          placePredictions={placePredictions}
          isPlacePredictionsLoading={isPlacePredictionsLoading}
          onPlaceSelected={handleOptionSelected}
          isMobile={isMobile}
          shortLabel={shortLabel}
          searchQuery={searchQuery}
          revampPicker={revampPicker}
          isLocationSelected={isLocationSelected}
        />
      )}
      {searchQueryIsEmpty && revampPicker && !!mostBookedRegions?.length && (
        <ListMostBookedRegions
          items={mostBookedRegions}
          onItemSelected={handleOptionSelected}
          isMobile={isMobile}
          isLocationSelected={isLocationSelected}
          titleTopDestinations={
            <FormattedMessage
              id='ui.ListMostBookedRegions.title'
              defaultMessage='Top Destinations'
            />
          }
        />
      )}
    </div>
  )
}
